'use client'

import {
  Page,
  Card,
  Layout,
  Text,
  Banner,
  BlockStack,
  Button,
  InlineStack,
  Spinner,
  Box,
  Tabs,
  Collapsible,
  Divider,
} from '@shopify/polaris'
import { useEffect, useLayoutEffect, useState, useCallback, useRef, useMemo } from 'react'
import { usePathname, useRouter, useSearchParams } from 'next/navigation'
import {
  useAuthenticatedFetch,
  useAppBridgeReady,
  clearSessionTokenCache,
} from '@/app/(app)/hooks/useAuthenticatedFetch'
import { redirectToUrl } from '@/app/(app)/hooks/useAppBridgeRedirect'
import { useBoot } from '@/app/(app)/context/BootContext'
import { useBootstrap, type BootstrapData } from '@/app/(app)/context/BootstrapContext'
import { MetricCard } from './MetricCard'
import { MetricCardSkeleton } from './MetricCardSkeleton'
import {
  getDashboardDefaultDateRange,
  formatPeriodLabel,
  resolveShopDisplayTimeZone,
} from '@/lib/dashboard/dateRange'
import {
  resolveReportingDatePresetRange,
  type ReportingDatePresetId,
} from '@/lib/dashboard/reportingDatePresets'
import type { FreshnessMeta } from '@/lib/analytics/freshness'
import { parseResponseJson } from '@/lib/client/parseResponseJson'
import { getDashboardReportingHelpSections } from '@/lib/reporting/reportingHelpCopy'
import { DASHBOARD_AUDIT_EXPORT_PERIOD_HINT } from '@/lib/reporting/reportingSharedCopy'
import { OVERVIEW_RTO_ATTENTION_PCT } from '@/lib/dashboard/overviewInsightThresholds'
import { SigmaEmptyState } from '@/components/sigma/SigmaEmptyState'
import { SigmaPeriodBar } from '@/components/sigma/SigmaPeriodBar'
import { DashboardOrdersExportMenu } from '@/components/dashboard/DashboardOrdersExportMenu'
import { DashboardPeriodRangeControl } from '@/components/dashboard/DashboardPeriodRangeControl'
import { ReportingHowThisWorksPopover } from '@/components/reporting/ReportingHowThisWorksPopover'
import {
  buildExpensesDrilldownUrl,
  buildMetricBreakdownUrl,
  buildOrdersDrilldownUrl,
  buildOrdersPendingShopWideUrl,
  buildOrdersInTransitShopWideUrl,
  buildRevenueDrilldownUrl,
} from '@/lib/dashboard/buildDashboardDrilldownUrls'
import { UsageBar } from '@/components/UsageBar'
import { triggerCsvDownload } from '@/lib/export/downloadCsv'
import {
  buildDashboardMovers,
  sliceMetricsForMovers,
  type DashboardMoverBullet,
} from '@/lib/dashboard/dashboardMovers'
import { DashboardDateProvider } from '@/app/(app)/context/DashboardDateContext'
import { DashboardTrendCard } from '@/components/dashboard/DashboardTrendCard'
import { DashboardQuickActions } from '@/components/dashboard/DashboardQuickActions'
import { DashboardOverviewInsights } from '@/components/dashboard/DashboardOverviewInsights'
import { SigmaSectionHeader } from '@/components/sigma/SigmaSectionHeader'
import { useSigmaSurface } from '@/components/SigmaSurfaceProvider'
import dynamic from 'next/dynamic'

// Lazy-load non-default tab content so their JS is not bundled with the dashboard
// critical path. Each chunk is downloaded only when the user first opens that tab.
const AnalyticsOverviewTabContent = dynamic(
  () => import('./AnalyticsOverviewTabContent').then((m) => ({ default: m.AnalyticsOverviewTabContent })),
  { ssr: false, loading: () => <Spinner size="small" accessibilityLabel="Loading analytics…" /> }
)
const CouriersTabContent = dynamic(
  () => import('@/app/(app)/analytics/CouriersTabContent').then((m) => ({ default: m.CouriersTabContent })),
  { ssr: false, loading: () => <Spinner size="small" accessibilityLabel="Loading couriers…" /> }
)
const AreasTabContent = dynamic(
  () => import('@/app/(app)/analytics/AreasTabContent').then((m) => ({ default: m.AreasTabContent })),
  { ssr: false, loading: () => <Spinner size="small" accessibilityLabel="Loading areas…" /> }
)

/** Dashboard metrics per docs/DASHBOARD_METRICS_SPEC.md */
interface DashboardMetrics {
  totalOrdersReceived: number
  totalFulfilledOrders: number
  totalCancelledOrders: number
  deliveredOrders: number
  returnedOrders: number
  /** Σ daily inTransitOrders; created-day bucket in shop TZ, not live pipeline snapshot. */
  inTransitOrders: number
  rtoRate: number
  totalRevenueAll: number
  totalRevenueActiveOrders: number
  totalRevenueFulfilled: number
  totalRevenueDelivered: number
  governmentTax: number
  courierCharges: number
  payfastExpenses: number
  adsExpenses: number
  operationalExpenses: number
  inventoryExpenses: number
  totalExpenses: number
  netProfit: number
  pendingPipeline?: {
    inRange: {
      orderCount: number
      revenue: number
      actualProfit: number
      projectedProfitIfDelivered: number
    }
    shopWide: {
      orderCount: number
      revenue: number
      actualProfit: number
      projectedProfitIfDelivered: number
    }
  }
  inTransitPipeline?: {
    inRange: {
      orderCount: number
      revenue: number
      actualProfit: number
      projectedProfitIfDelivered: number
    }
    shopWide: {
      orderCount: number
      revenue: number
      actualProfit: number
      projectedProfitIfDelivered: number
    }
  }
}

interface GetStartedStatusSignals {
  ordersCount: number
  skuCostsCount: number
  courierCredentialsEnabledCount: number
}

/** Safely coerce unknown API value to number */
function toNum(v: unknown): number {
  return typeof v === 'number' && !Number.isNaN(v) ? v : 0
}

/** Only accept live raw-order rollup payloads from `/api/analytics/dashboard` (never daily-summary cache shapes). */
function isRawDashboardPayload(data: unknown): boolean {
  if (!data || typeof data !== 'object') return false
  const d = data as Record<string, unknown>
  return (
    typeof d.totalOrdersReceived === 'number' &&
    typeof d.totalRevenueAll === 'number' &&
    typeof d.netProfit === 'number'
  )
}

function normalizeMetrics(data: Partial<DashboardMetrics> | Record<string, unknown> | null | undefined): DashboardMetrics | null {
  if (data == null) return null
  const d = data as Record<string, unknown>
  if (!isRawDashboardPayload(d)) return null
  return {
    totalOrdersReceived: toNum(d?.totalOrdersReceived),
    totalFulfilledOrders: toNum(d?.totalFulfilledOrders),
    totalCancelledOrders: toNum(d?.totalCancelledOrders),
    deliveredOrders: toNum(d?.deliveredOrders),
    returnedOrders: toNum(d?.returnedOrders),
    inTransitOrders: toNum(d?.inTransitOrders),
    rtoRate: toNum(d?.rtoRate),
    totalRevenueAll: toNum(d?.totalRevenueAll),
    totalRevenueActiveOrders:
      typeof d?.totalRevenueActiveOrders === 'number'
        ? toNum(d.totalRevenueActiveOrders)
        : toNum(d?.totalRevenueAll),
    totalRevenueFulfilled: toNum(d?.totalRevenueFulfilled),
    totalRevenueDelivered: toNum(d?.totalRevenueDelivered),
    governmentTax: toNum(d?.governmentTax),
    courierCharges: toNum(d?.courierCharges),
    payfastExpenses: toNum(d?.payfastExpenses),
    adsExpenses: toNum(d?.adsExpenses),
    operationalExpenses: toNum(d?.operationalExpenses),
    inventoryExpenses: toNum(d?.inventoryExpenses),
    totalExpenses: toNum(d?.totalExpenses),
    netProfit: toNum(d?.netProfit),
    pendingPipeline:
      d?.pendingPipeline && typeof d.pendingPipeline === 'object'
        ? (d.pendingPipeline as DashboardMetrics['pendingPipeline'])
        : undefined,
    inTransitPipeline:
      d?.inTransitPipeline && typeof d.inTransitPipeline === 'object'
        ? (d.inTransitPipeline as DashboardMetrics['inTransitPipeline'])
        : undefined,
  }
}

const APP_URL = process.env.NEXT_PUBLIC_APP_URL || 'https://app.sigmasense.pk'
const FETCH_TIMEOUT = 15000
const DASHBOARD_INIT_MAX_ATTEMPTS = 3
/** Cap idle wait for deferred /api/billing/usage when optimistic usage already shown (~1–2s). */
const USAGE_DEFER_IDLE_FALLBACK_MS = 1800

/**
 * Run `fn` once after idle, with setTimeout backstop. Whichever runs first wins; cleanup cancels both.
 */
function scheduleDeferredTask(fn: () => void, isCancelled: () => boolean): () => void {
  let fired = false
  let idleId: number | undefined
  let timeoutId: ReturnType<typeof setTimeout> | undefined

  const clear = () => {
    if (timeoutId !== undefined) {
      clearTimeout(timeoutId)
      timeoutId = undefined
    }
    if (idleId !== undefined && typeof window !== 'undefined' && 'cancelIdleCallback' in window) {
      window.cancelIdleCallback(idleId)
      idleId = undefined
    }
  }

  const runOnce = () => {
    if (fired || isCancelled()) return
    fired = true
    clear()
    fn()
  }

  if (typeof window !== 'undefined' && typeof window.requestIdleCallback === 'function') {
    idleId = window.requestIdleCallback(runOnce, { timeout: USAGE_DEFER_IDLE_FALLBACK_MS })
  }
  timeoutId = setTimeout(runOnce, USAGE_DEFER_IDLE_FALLBACK_MS)

  return clear
}


/** Bump when dashboard metric shape or source-of-truth changes (invalidates sessionStorage SWR cache). */
const DASHBOARD_CACHE_PREFIX = 'dashboardMetrics:v4:raw_orders:'
const USAGE_CACHE_PREFIX = 'dashboardUsage:v2:'
const USAGE_CACHE_TTL_MS = 60 * 1000

function getNextRefreshBoundary(): number {
  const now = new Date()
  const y = now.getUTCFullYear()
  const m = now.getUTCMonth()
  const d = now.getUTCDate()
  const noon = Date.UTC(y, m, d, 12, 0, 0, 0)
  const ten = Date.UTC(y, m, d, 22, 0, 0, 0)
  if (now.getTime() < noon) return noon
  if (now.getTime() < ten) return ten
  return Date.UTC(y, m, d + 1, 12, 0, 0, 0)
}

function getDashboardCacheKey(shop: string, dateFrom: string, dateTo: string): string {
  return `${DASHBOARD_CACHE_PREFIX}${shop}:${dateFrom}:${dateTo}`
}

function getCachedDashboard(shop: string, dateFrom: string, dateTo: string): { current: unknown; previous: unknown } | null {
  if (typeof window === 'undefined') return null
  try {
    const key = getDashboardCacheKey(shop, dateFrom, dateTo)
    const raw = sessionStorage.getItem(key)
    if (!raw) return null
    const { data, expiresAt } = JSON.parse(raw) as { data?: { current?: unknown; previous?: unknown }; expiresAt?: number }
    if (typeof expiresAt !== 'number' || Date.now() >= expiresAt) return null
    if (data && typeof data === 'object' && ('current' in data || 'previous' in data)) return data as { current: unknown; previous: unknown }
    return null
  } catch {
    return null
  }
}

function setCachedDashboard(
  shop: string,
  dateFrom: string,
  dateTo: string,
  payload: { current: unknown; previous: unknown },
  /** Explicit expiry timestamp. Defaults to next refresh boundary (noon/22:00 UTC) for the default range. */
  expiresAt?: number
): void {
  if (typeof window === 'undefined') return
  try {
    const key = getDashboardCacheKey(shop, dateFrom, dateTo)
    sessionStorage.setItem(key, JSON.stringify({ data: payload, expiresAt: expiresAt ?? getNextRefreshBoundary() }))
  } catch {}
}

function getCachedUsage(shop: string): { plan: string; usedOrders: number; orderLimit: number | null; remainingOrders: number; percentUsed: number } | null {
  if (typeof window === 'undefined') return null
  try {
    const key = `${USAGE_CACHE_PREFIX}${shop}`
    const raw = sessionStorage.getItem(key)
    if (!raw) return null
    const { data, expiresAt } = JSON.parse(raw) as { data?: Record<string, unknown>; expiresAt?: number }
    if (typeof expiresAt !== 'number' || Date.now() >= expiresAt) return null
    if (data && typeof data === 'object')
      return {
        plan: (data.plan as string) ?? 'free',
        usedOrders: Number(data.usedOrders) ?? 0,
        orderLimit: data.orderLimit != null ? Number(data.orderLimit) : null,
        remainingOrders: Number(data.remainingOrders) ?? 0,
        percentUsed: Number(data.percentUsed) ?? 0,
      }
    return null
  } catch {
    return null
  }
}

function setCachedUsage(shop: string, data: { plan: string; usedOrders: number; orderLimit: number | null; remainingOrders: number; percentUsed: number }): void {
  if (typeof window === 'undefined') return
  try {
    const key = `${USAGE_CACHE_PREFIX}${shop}`
    sessionStorage.setItem(key, JSON.stringify({ data, expiresAt: Date.now() + USAGE_CACHE_TTL_MS }))
  } catch {}
}

/** Bootstrap `usage` is from shop_monthly_usage_summary (denormalized); first paint only. */
function buildUsagePlaceholderFromBootstrap(data: BootstrapData | null | undefined): {
  plan: string
  usedOrders: number
  orderLimit: number | null
  remainingOrders: number
  percentUsed: number
} | null {
  if (!data?.usage) return null
  const u = data.usage
  const orderLimit = u.allowed
  const percentUsed = orderLimit > 0 ? Math.min(100, (u.used / orderLimit) * 100) : 0
  return {
    plan: (data.plan?.planName ?? 'free').toLowerCase(),
    usedOrders: u.used,
    orderLimit,
    remainingOrders: u.remaining,
    percentUsed,
  }
}

const DASHBOARD_TABS = [
  { id: 'overview', content: 'Overview', panelID: 'dashboard-overview-panel' },
  { id: 'analytics', content: 'Analytics', panelID: 'dashboard-analytics-panel' },
  { id: 'couriers', content: 'Couriers', panelID: 'dashboard-couriers-panel' },
  { id: 'areas', content: 'Areas', panelID: 'dashboard-areas-panel' },
]

const DASHBOARD_TAB_IDS = DASHBOARD_TABS.map((t) => t.id)

export default function DashboardPage() {
  const pathname = usePathname()
  const router = useRouter()
  const searchParams = useSearchParams()
  const { bootShop: shop, bootHost: host, portalSessionStatus } = useBoot()
  const sigmaSurface = useSigmaSurface()
  const isPortalSurface = sigmaSurface === 'portal'
  /** Embedded needs shop+host; portal uses cookies and omits Shopify host. */
  const sessionReady = Boolean(shop && (host || isPortalSurface))

  const billingHref = useMemo(() => {
    if (!shop || !sessionReady) return '/billing'
    if (host) return `/billing?shop=${encodeURIComponent(shop)}&host=${encodeURIComponent(host)}`
    return `/billing?shop=${encodeURIComponent(shop)}`
  }, [shop, host, sessionReady])

  // Dashboard tab state — ?tab=overview|analytics|couriers|areas
  // Separate from createdAtMin/createdAtMax date params — no collision
  const dashTabParam = searchParams.get('tab') ?? 'overview'
  const dashTabIndex = DASHBOARD_TAB_IDS.includes(dashTabParam) ? DASHBOARD_TAB_IDS.indexOf(dashTabParam) : 0
  const handleDashboardTabChange = useCallback(
    (idx: number) => {
      const next = new URLSearchParams(searchParams.toString())
      next.set('tab', DASHBOARD_TAB_IDS[idx] ?? 'overview')
      router.replace(`${pathname}?${next.toString()}`)
    },
    [pathname, router, searchParams]
  )
  const bootstrap = useBootstrap()
  const defaultRange = bootstrap.data?.dashboardDefaultRange ?? getDashboardDefaultDateRange()
  const [dateFrom, setDateFrom] = useState(() => getDashboardDefaultDateRange().from)
  const [dateTo, setDateTo] = useState(() => getDashboardDefaultDateRange().to)
  const [metrics, setMetrics] = useState<DashboardMetrics | null>(null)
  const [previousMetrics, setPreviousMetrics] = useState<DashboardMetrics | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [reauthUrl, setReauthUrl] = useState<string | null>(null)
  const [authError, setAuthError] = useState(false)
  const [retryTrigger, setRetryTrigger] = useState(0)
  const [pendingMessage, setPendingMessage] = useState<string | null>(null)
  const [orderDiagnostics, setOrderDiagnostics] = useState<{
    ordersInDatabaseForRange: number
  } | null>(null)
  const authenticatedFetch = useAuthenticatedFetch()
  const appBridgeReady = useAppBridgeReady()
  const [usageLoading, setUsageLoading] = useState(true)
  const [usageData, setUsageData] = useState<{
    plan: string
    usedOrders: number
    orderLimit: number | null
    remainingOrders: number
    percentUsed: number
  } | null>(null)
  const [coreSetupComplete, setCoreSetupComplete] = useState<boolean>(false)
  const [auditExporting, setAuditExporting] = useState(false)
  const [auditExportMessage, setAuditExportMessage] = useState<string | null>(null)
  /** True when a background refetch is running but existing metrics are already visible. */
  const [isRefreshing, setIsRefreshing] = useState(false)
  /** Comparison period (previous range) is still loading after current-period KPIs painted. */
  const [isPreviousPeriodLoading, setIsPreviousPeriodLoading] = useState(false)
  /** All metrics — progressive disclosure per domain. */
  const [metricsOrdersOpen, setMetricsOrdersOpen] = useState(false)
  const [metricsDeliveryOpen, setMetricsDeliveryOpen] = useState(false)
  const [metricsRevenueOpen, setMetricsRevenueOpen] = useState(false)
  const [metricsExpensesOpen, setMetricsExpensesOpen] = useState(false)
  const [metricsFinancialOpen, setMetricsFinancialOpen] = useState(false)
  const mountedRef = useRef(true)
  const hasRedirectedRef = useRef(false)
  const hasAuthFailedRef = useRef(false)
  /** After GET /api/billing/usage succeeds, do not replace with bootstrap/cache on re-renders. */
  const usageFromBillingApiRef = useRef(false)
  const usageShopHostKeyRef = useRef<string | null>(null)
  /**
   * Captures the bootstrap-derived usage placeholder so the main usage fetch effect
   * can read it without taking bootstrap.data as a dep (which would cause the effect
   * to re-fire every time the bootstrap reference changes, e.g. cache → live).
   */
  const bootstrapUsageRef = useRef<ReturnType<typeof buildUsagePlaceholderFromBootstrap>>(null)
  /** Once we've settled (loading=false), never show skeleton again on background refresh */
  const hasSettledRef = useRef(false)
  /** When a preset updates URL+state, skip duplicate fetch from URL layout sync */
  const pendingPresetUrlRef = useRef<string | null>(null)
  /**
   * Tracks the `${dateFrom}|${dateTo}` key of the most recently initiated fetch.
   * The fetch effect compares against this before starting a new request so that
   * a date-range change (e.g. bootstrap TZ correction) doesn't produce a second
   * in-flight request for the same range. Reset to null by handleLoadDashboard so
   * forced reloads always proceed regardless of the current key.
   */
  const lastFetchedDateKeyRef = useRef<string | null>(null)
  /** After first successful dashboard metrics settle for shop|host, get-started runs once (not on every date change). */
  const getStartedScheduledShopHostRef = useRef<string | null>(null)

  const shopDisplayTz = useMemo(
    () => resolveShopDisplayTimeZone(bootstrap.data?.dashboardDefaultRange?.timeZone),
    [bootstrap.data?.dashboardDefaultRange?.timeZone]
  )

  /** Narrow dep: avoid re-running the metrics effect when unrelated bootstrap fields change. */
  const bootstrapMetricsDepKey = useMemo(
    () =>
      [
        bootstrap.data?.dashboardDefaultRange?.from ?? '',
        bootstrap.data?.dashboardDefaultRange?.to ?? '',
        bootstrap.data?.dashboardDefaultRange?.timeZone ?? '',
      ].join('|'),
    [
      bootstrap.data?.dashboardDefaultRange?.from,
      bootstrap.data?.dashboardDefaultRange?.to,
      bootstrap.data?.dashboardDefaultRange?.timeZone,
    ]
  )

  // Sync to server default range once when bootstrap provides it (avoids UTC-day prefill mismatch).
  // Skipped when /dashboard?createdAtMin=&createdAtMax= is applied (see useLayoutEffect below).
  const hasSyncedToServerDefaultRef = useRef(false)
  const triggerFetch = useCallback(() => setRetryTrigger((t) => t + 1), [])

  const urlCreatedAtMin = (searchParams.get('createdAtMin') ?? '').trim()
  const urlCreatedAtMax = (searchParams.get('createdAtMax') ?? '').trim()

  useLayoutEffect(() => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(urlCreatedAtMin) || !/^\d{4}-\d{2}-\d{2}$/.test(urlCreatedAtMax)) return
    const key = `${urlCreatedAtMin}|${urlCreatedAtMax}`
    if (pendingPresetUrlRef.current === key) {
      pendingPresetUrlRef.current = null
      return
    }
    if (urlCreatedAtMin === dateFrom && urlCreatedAtMax === dateTo) {
      hasSyncedToServerDefaultRef.current = true
      return
    }
    hasSyncedToServerDefaultRef.current = true
    let changed = false
    if (urlCreatedAtMin !== dateFrom) {
      setDateFrom(urlCreatedAtMin)
      changed = true
    }
    if (urlCreatedAtMax !== dateTo) {
      setDateTo(urlCreatedAtMax)
      changed = true
    }
    if (changed) triggerFetch()
  }, [urlCreatedAtMin, urlCreatedAtMax, dateFrom, dateTo, triggerFetch])

  useEffect(() => {
    const serverRange = bootstrap.data?.dashboardDefaultRange
    if (!serverRange || hasSyncedToServerDefaultRef.current) return
    const client = getDashboardDefaultDateRange()
    if (dateFrom === client.from && dateTo === client.to) {
      hasSyncedToServerDefaultRef.current = true
      const fromChanged = serverRange.from !== dateFrom
      const toChanged = serverRange.to !== dateTo
      if (fromChanged) setDateFrom(serverRange.from)
      if (toChanged) setDateTo(serverRange.to)
      // If the server range differs from what was already fetched, the fetch
      // effect will re-run via the dateFrom/dateTo deps and the dedup ref will
      // allow it through (different key). No explicit triggerFetch() needed here.
    }
  }, [bootstrap.data?.dashboardDefaultRange, dateFrom, dateTo])

  const POLL_BASE_INTERVAL_MS = 10000
  const POLL_MAX_INTERVAL_MS = 25000
  const POLL_CAP_MS = 2 * 60 * 1000

  // When dashboard API returned pending, poll status/analytics; when running becomes false, refetch once.
  useEffect(() => {
    if (!sessionReady || !pendingMessage) return
    let timeoutId: ReturnType<typeof setTimeout> | null = null
    let cancelled = false
    let inFlight = false
    let attempts = 0
    const startedAt = Date.now()
    const stop = () => {
      cancelled = true
      if (timeoutId) clearTimeout(timeoutId)
    }
    const scheduleNext = () => {
      if (cancelled) return
      attempts += 1
      const backoff = Math.min(POLL_MAX_INTERVAL_MS, POLL_BASE_INTERVAL_MS * Math.pow(2, Math.max(0, attempts - 1)))
      const jitter = Math.floor(Math.random() * 1200)
      timeoutId = setTimeout(poll, backoff + jitter)
    }
    const poll = async () => {
      if (cancelled || inFlight) return
      if (Date.now() - startedAt > POLL_CAP_MS) {
        stop()
        return
      }
      inFlight = true
      try {
        const apiBase = typeof window !== 'undefined' ? window.location.origin : ''
        const stQs = new URLSearchParams({ shop: shop! })
        if (host) stQs.set('host', host)
        const res = await authenticatedFetch(`${apiBase}/api/status/analytics?${stQs.toString()}`)
        if (res.status === 401) {
          const authUrlFallback = host
            ? `${APP_URL}/api/auth?shop=${encodeURIComponent(shop!)}&host=${encodeURIComponent(host)}`
            : `${typeof window !== 'undefined' ? window.location.origin.replace(/\/$/, '') : ''}/login`
          setReauthUrl(authUrlFallback)
          setAuthError(true)
          stop()
          return
        }
        const st = await parseResponseJson<{ running?: boolean }>(res)
        if (!mountedRef.current) return
        if (res.ok && st?.running === false) {
          stop()
          setPendingMessage(null)
          triggerFetch()
          return
        }
      } catch {
        // ignore; next poll will retry
      } finally {
        inFlight = false
      }
      scheduleNext()
    }
    poll()
    return () => {
      stop()
    }
  }, [shop, host, sessionReady, pendingMessage, authenticatedFetch, triggerFetch])

  const usage = {
    loading: usageLoading,
    plan: usageData?.plan ?? 'free',
    usedOrders: usageData?.usedOrders ?? 0,
    orderLimit: usageData?.orderLimit ?? null,
    remainingOrders: usageData?.remainingOrders ?? 0,
    percentUsed: usageData?.percentUsed ?? 0,
  }

  useEffect(() => {
    mountedRef.current = true
    return () => { mountedRef.current = false }
  }, [])

  useEffect(() => {
    if (!shop) {
      setCoreSetupComplete(false)
      getStartedScheduledShopHostRef.current = null
      return
    }
    if (!host && !isPortalSurface) {
      setCoreSetupComplete(false)
      getStartedScheduledShopHostRef.current = null
    }
  }, [shop, host, isPortalSurface])

  // Redirect effect: when authError, trigger single App Bridge REMOTE redirect once (hasRedirected guard).
  // Only after App Bridge is ready; no redirect during render.
  useEffect(() => {
    if (!authError || hasRedirectedRef.current) return
    if (host && !appBridgeReady) return
    const origin = typeof window !== 'undefined' ? window.location.origin.replace(/\/$/, '') : ''
    const url =
      reauthUrl ||
      (host && shop
        ? `${APP_URL}/api/auth?shop=${encodeURIComponent(shop)}&host=${encodeURIComponent(host)}`
        : shop && isPortalSurface
          ? `${origin}/login`
          : shop
            ? `${APP_URL.replace(/\/$/, '')}/signin`
            : null)
    if (!url) return
    hasRedirectedRef.current = true
    redirectToUrl(url, { host: host ?? undefined, shop: shop ?? undefined })
  }, [authError, reauthUrl, host, shop, appBridgeReady, isPortalSurface])

  // Keep bootstrapUsageRef in sync with bootstrap.data so the usage fetch effect can
  // read the optimistic placeholder without taking bootstrap.data as a direct dep.
  useEffect(() => {
    bootstrapUsageRef.current = buildUsagePlaceholderFromBootstrap(bootstrap.data)
  }, [bootstrap.data])

  // Usage card + limit banner: bootstrap/cache are optimistic first paint only (summary may drift).
  // GET /api/billing/usage is authoritative (getUsageCount); always fetched; overwrites on success.
  // When optimistic usage already exists, defer the billing request to reduce overlap with dashboard KPI fetch.
  // bootstrap.data is intentionally NOT in this dep array — changes to the bootstrap reference
  // (e.g. cache → live resolution) must not re-trigger this fetch. The bootstrapUsageRef above
  // keeps the optimistic placeholder available without causing extra runs.
  useEffect(() => {
    if (!sessionReady || !shop) {
      setUsageLoading(false)
      return
    }

    /** Portal: wait for hydrated session; avoid billing/usage until we know real bootstrap. */
    if (isPortalSurface && portalSessionStatus === 'loading') {
      const placeholder = bootstrapUsageRef.current ?? getCachedUsage(shop)
      if (placeholder) {
        setUsageData(placeholder)
        setUsageLoading(false)
      } else {
        setUsageLoading(true)
      }
      return
    }

    /** Portal: usage already in /api/portal/session?hydrate=1 bootstrap — skip extra billing API. */
    if (
      isPortalSurface &&
      bootstrap.data?.planResolution === 'known' &&
      bootstrap.data.usage != null
    ) {
      const u = bootstrap.data.usage
      const orderLimit = u.allowed
      const payload = {
        plan: (bootstrap.data.plan?.planName ?? 'free').toLowerCase(),
        usedOrders: u.used,
        orderLimit,
        remainingOrders: u.remaining,
        percentUsed: orderLimit > 0 ? Math.min(100, (u.used / orderLimit) * 100) : 0,
      }
      setUsageData(payload)
      setCachedUsage(shop, payload)
      setUsageLoading(false)
      return
    }

    const shopId = shop
    const shopHostKey = `${shopId}|${host ?? 'cookie'}`
    if (usageShopHostKeyRef.current !== shopHostKey) {
      usageShopHostKeyRef.current = shopHostKey
      usageFromBillingApiRef.current = false
    }

    let cancelled = false
    let cancelDeferred: (() => void) | undefined

    if (!usageFromBillingApiRef.current) {
      const optimistic = bootstrapUsageRef.current ?? getCachedUsage(shopId)
      if (optimistic) {
        setUsageData(optimistic)
        setUsageLoading(false)
      } else {
        setUsageData(null)
        setUsageLoading(true)
      }
    }

    const run = async () => {
      try {
        const base = typeof window !== 'undefined' ? window.location.origin : ''
        const uq = new URLSearchParams({ shop: shopId })
        if (host) uq.set('host', host)
        const url = `${base}/api/billing/usage?${uq.toString()}`
        const res = await authenticatedFetch(url)
        const data = await parseResponseJson<{
          usagePending?: boolean
          plan?: string
          usedOrders?: number
          orderLimit?: number | null
          remainingOrders?: number
          percentUsed?: number
        }>(res)
        if (cancelled || !mountedRef.current) return
        if (res.ok && !data.usagePending) {
          usageFromBillingApiRef.current = true
          const payload = {
            plan: data.plan ?? 'free',
            usedOrders: data.usedOrders ?? 0,
            orderLimit: data.orderLimit ?? null,
            remainingOrders: data.remainingOrders ?? 0,
            percentUsed: data.percentUsed ?? 0,
          }
          setUsageData(payload)
          setCachedUsage(shopId, payload)
        }
      } catch {
        // Keep optimistic bootstrap/cache or prior API snapshot; do not blank the card.
      } finally {
        if (mountedRef.current) setUsageLoading(false)
      }
    }

    const shouldDeferUsage =
      !usageFromBillingApiRef.current &&
      (bootstrapUsageRef.current != null || getCachedUsage(shopId) != null)

    if (shouldDeferUsage) {
      cancelDeferred = scheduleDeferredTask(() => void run(), () => cancelled)
    } else {
      void run()
    }

    return () => {
      cancelled = true
      cancelDeferred?.()
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shop, host, sessionReady, authenticatedFetch, isPortalSurface, portalSessionStatus, bootstrap.data])

  // API fetch effect: runs on mount and whenever the effective date range changes.
  // Guards: shop+host required; hasAuthFailedRef stops after auth failure;
  // lastFetchedDateKeyRef deduplicates runs for the same dateFrom|dateTo so that React
  // strict-mode double-invokes and rapid date state updates (e.g. bootstrap TZ correction
  // landing in the same render cycle) don't produce two simultaneous in-flight requests.
  // retryTrigger (incremented by handleLoadDashboard) resets lastFetchedDateKeyRef to null,
  // which allows a forced reload for the same range.
  useEffect(() => {
    if (!sessionReady) {
      setError('Missing shop or host parameter')
      setLoading(false)
      hasSettledRef.current = true
      return
    }
    const shopDomain = shop!
    if (hasAuthFailedRef.current) return

    // Dedup guard: skip if we already initiated a fetch for this exact date range.
    // handleLoadDashboard resets this to null so manual reloads always proceed.
    const fetchDateKey = `${dateFrom}|${dateTo}`
    if (lastFetchedDateKeyRef.current === fetchDateKey) return

    const apiBase = typeof window !== 'undefined' ? window.location.origin : ''
    const params = new URLSearchParams()
    params.set('shop', shopDomain)
    if (host) params.set('host', host)
    params.set('createdAtMin', dateFrom)
    params.set('createdAtMax', dateTo)
    const qs = params.toString()
    const authUrlFallback = host
      ? `${APP_URL}/api/auth?shop=${encodeURIComponent(shopDomain)}&host=${encodeURIComponent(host)}`
      : `${typeof window !== 'undefined' ? window.location.origin.replace(/\/$/, '') : ''}/login`

    const fetchWithTimeout = async (url: string) => {
      const controller = new AbortController()
      const tid = setTimeout(() => controller.abort(), FETCH_TIMEOUT)
      try {
        const resp = await authenticatedFetch(url, { signal: controller.signal })
        clearTimeout(tid)
        return resp
      } catch (err) {
        clearTimeout(tid)
        throw err
      }
    }

    const setAuthFailed = () => {
      hasAuthFailedRef.current = true
      setAuthError(true)
      setLoading(false)
      setIsRefreshing(false)
      hasSettledRef.current = true
    }

    const settle = (options?: { preserveRefreshing?: boolean }) => {
      setLoading(false)
      if (!options?.preserveRefreshing) setIsRefreshing(false)
      hasSettledRef.current = true
    }

    let cancelled = false
    if (!hasSettledRef.current) setLoading(true)
    setError(null)
    setReauthUrl(null)

    const cachedDashboard = getCachedDashboard(shopDomain, dateFrom, dateTo)
    const rawCached =
      cachedDashboard &&
      isRawDashboardPayload(cachedDashboard.current) &&
      (cachedDashboard.previous == null || isRawDashboardPayload(cachedDashboard.previous))
    if (rawCached && cachedDashboard) {
      setMetrics(normalizeMetrics(cachedDashboard.current as Record<string, unknown>))
      setPreviousMetrics(normalizeMetrics(cachedDashboard.previous as Record<string, unknown> | null))
    }
    const hasDataToShow = Boolean(rawCached)
    if (hasDataToShow) {
      setLoading(false)
      hasSettledRef.current = true
    }
    // Claim this range so strict-mode remount / rapid updates don't double-fetch; cleared on cleanup.
    lastFetchedDateKeyRef.current = fetchDateKey

    // Fetch from API so UI stays in sync with server; when we already have data we don't block first paint
    const shouldDeferRevalidate =
      hasDataToShow && retryTrigger === 0

    const run = async () => {
      /** Stale-while-revalidate: keep "Updating" after settle() until a fresh (non-stale) response. */
      let preserveRefreshingAfterSettle = false
      if (typeof window !== 'undefined' && process.env.NEXT_PUBLIC_DEBUG_DASHBOARD === '1') {
        console.log('[dashboard] fetch start', { shop, dateFrom, dateTo })
      }
      try {
        setIsPreviousPeriodLoading(false)

        const readReauth = (d: Record<string, unknown>) =>
          typeof d.reauthUrl === 'string' ? d.reauthUrl : null

        const handleCommonErrors = (d: Record<string, unknown>, resp: Response): boolean => {
          if (d?.initializing === true && !d?.reason) {
            setError('Still connecting. Click Retry or refresh.')
            settle()
            return false
          }
          const reauthUrlFromBody = readReauth(d)
          if (d?.reauth && reauthUrlFromBody) {
            setReauthUrl(reauthUrlFromBody)
            setOrderDiagnostics(null)
            setAuthFailed()
            return false
          }
          if (!resp.ok) {
            setOrderDiagnostics(null)
            if (resp.status === 401) {
              setReauthUrl(reauthUrlFromBody || authUrlFallback)
              setAuthFailed()
            } else {
              setError(typeof d.error === 'string' ? d.error : 'Failed to load metrics')
              settle()
            }
            return false
          }
          return true
        }

        const applyPendingBanner = (d: Record<string, unknown>) => {
          const apiFreshness = (d as { freshness?: FreshnessMeta }).freshness
          const freshnessReason = apiFreshness?.reason
          if (
            d?.pending &&
            d?.message &&
            freshnessReason !== 'recent_completion_suppressed' &&
            freshnessReason !== 'kill_switch' &&
            freshnessReason !== 'no_cache_manual_refresh_required' &&
            freshnessReason !== 'ttl_expired_manual_refresh_required'
          ) {
            setPendingMessage(d.message as string)
          } else {
            setPendingMessage(null)
          }
        }

        const applyDiagnostics = (d: Record<string, unknown>) => {
          const rawDiag = d.diagnostics as Record<string, unknown> | undefined
          if (rawDiag && typeof rawDiag.ordersInDatabaseForRange === 'number') {
            setOrderDiagnostics({ ordersInDatabaseForRange: rawDiag.ordersInDatabaseForRange })
          }
        }

        // Phase 1 — current range only (shows KPIs without waiting on the comparison period rollup).
        let metricsResp!: Response
        let metricsData: Record<string, unknown> = {}
        for (let initAttempt = 0; initAttempt < DASHBOARD_INIT_MAX_ATTEMPTS; initAttempt++) {
          if (cancelled || !mountedRef.current) return
          const pCurrent = new URLSearchParams(qs)
          pCurrent.set('segments', 'current')
          metricsResp = await fetchWithTimeout(`${apiBase}/api/analytics/dashboard?${pCurrent.toString()}`)
          metricsData = (await parseResponseJson(metricsResp)) as Record<string, unknown>
          if (cancelled || !mountedRef.current) return
          const stuckInit = metricsData?.initializing === true && !metricsData?.reason
          if (!stuckInit) break
          if (initAttempt < DASHBOARD_INIT_MAX_ATTEMPTS - 1) {
            clearSessionTokenCache()
            await new Promise((r) => setTimeout(r, 400 * (initAttempt + 1)))
          }
        }

        if (cancelled || !mountedRef.current) return

        if (typeof window !== 'undefined' && process.env.NEXT_PUBLIC_DEBUG_DASHBOARD === '1') {
          console.log('[dashboard] fetch phase1 done', { ok: metricsResp.ok, pending: !!metricsData?.pending })
        }

        if (!handleCommonErrors(metricsData, metricsResp)) return

        applyPendingBanner(metricsData)
        applyDiagnostics(metricsData)

        const currentPayload = metricsData?.current ?? metricsData
        setMetrics(normalizeMetrics(currentPayload))
        setPreviousMetrics(null)

        if (metricsData?.stale === true) {
          preserveRefreshingAfterSettle = true
          setIsRefreshing(true)
        }
        if (!hasDataToShow) settle()

        // Phase 2 — previous comparison period (background vs first paint).
        let mergedPrevious: unknown = null
        setIsPreviousPeriodLoading(true)
        try {
          const pPrev = new URLSearchParams(qs)
          pPrev.set('segments', 'previous')
          const prevResp = await fetchWithTimeout(`${apiBase}/api/analytics/dashboard?${pPrev.toString()}`)
          const prevData = (await parseResponseJson(prevResp)) as Record<string, unknown>
          if (cancelled || !mountedRef.current) return

          if (handleCommonErrors(prevData, prevResp)) {
            applyDiagnostics(prevData)
            mergedPrevious = prevData?.previous ?? null
            setPreviousMetrics(normalizeMetrics(mergedPrevious as Record<string, unknown> | null))
            if (prevData?.stale === true) {
              preserveRefreshingAfterSettle = true
              setIsRefreshing(true)
            }
          }
        } finally {
          setIsPreviousPeriodLoading(false)
        }

        if (!metricsData?.pending) {
          const cacheExpiresAt = Date.now() + 90 * 1000
          setCachedDashboard(
            shopDomain,
            dateFrom,
            dateTo,
            { current: currentPayload, previous: mergedPrevious },
            cacheExpiresAt
          )
        }
      } catch (err: any) {
        if (cancelled || !mountedRef.current) return
        setOrderDiagnostics(null)
        if (err.name === 'AbortError') {
          setError('Request timed out. Click Retry.')
        } else if (
          String(err.message || '').includes('Session token timeout') ||
          String(err.message || '').includes('App Bridge')
        ) {
          setReauthUrl(authUrlFallback)
          setAuthFailed()
        } else {
          setError(err.message || 'Failed to load dashboard')
        }
      } finally {
        if (mountedRef.current) settle({ preserveRefreshing: preserveRefreshingAfterSettle })
        // Portal: skip get-started status (heavy DB); embedded Admin still loads it for setup hints.
        if (!cancelled && mountedRef.current && host) {
          const shk = `${shopDomain}|${host ?? 'portal'}`
          if (getStartedScheduledShopHostRef.current !== shk) {
            getStartedScheduledShopHostRef.current = shk
            const runGetStarted = async () => {
              try {
                const apiBase = typeof window !== 'undefined' ? window.location.origin : ''
                const gs = new URLSearchParams({ shop: shopDomain })
                if (host) gs.set('host', host)
                const res = await authenticatedFetch(`${apiBase}/api/status/get-started?${gs.toString()}`)
                if (!res.ok) return
                const data = (await parseResponseJson(res)) as { signals?: GetStartedStatusSignals }
                if (!mountedRef.current || !data.signals) return
                const complete =
                  (data.signals.ordersCount ?? 0) > 0 &&
                  (data.signals.skuCostsCount ?? 0) > 0 &&
                  (data.signals.courierCredentialsEnabledCount ?? 0) > 0
                setCoreSetupComplete(complete)
              } catch {
                // Keep prompt in default state when status fetch fails.
              }
            }
            const idleFetch = () => {
              if (typeof window !== 'undefined' && typeof window.requestIdleCallback === 'function') {
                window.requestIdleCallback(() => void runGetStarted(), { timeout: 2000 })
              } else {
                void runGetStarted()
              }
            }
            idleFetch()
          }
        }
      }
    }

    let cancelDeferred: (() => void) | undefined
    if (shouldDeferRevalidate) {
      cancelDeferred = scheduleDeferredTask(() => {
        if (!cancelled && mountedRef.current) void run()
      }, () => cancelled)
    } else {
      void run()
    }

    return () => {
      cancelled = true
      cancelDeferred?.()
      lastFetchedDateKeyRef.current = null
    }
  // dateFrom/dateTo included: fetch re-runs when range changes (e.g. bootstrap TZ correction,
  // preset selection, URL param sync). lastFetchedDateKeyRef prevents duplicate fetches for
  // the same range; retryTrigger forces a reload even for the same range.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shop, host, sessionReady, isPortalSurface, authenticatedFetch, retryTrigger, bootstrapMetricsDepKey, dateFrom, dateTo])

  const handleLoadDashboard = useCallback(() => {
    // Clear the dedup guard so the fetch effect re-runs even for the same date range.
    lastFetchedDateKeyRef.current = null
    hasSettledRef.current = false
    setError(null)
    setPendingMessage(null)
    setIsPreviousPeriodLoading(false)
    setOrderDiagnostics(null)
    // Keep existing metrics visible when they are present — show refresh badge instead of skeleton.
    // Only fall back to full skeleton (loading=true) when there is genuinely no data to display.
    if (metrics) {
      setIsRefreshing(true)
    } else {
      setLoading(true)
    }
    triggerFetch()
  }, [triggerFetch, metrics])

  const handleExportAuditCsv = useCallback(
    async (segment?: 'delivered' | 'returned' | 'cancelled') => {
      if (!sessionReady || !shop || authError) return
      setAuditExporting(true)
      setAuditExportMessage(null)
      try {
        const apiBase = typeof window !== 'undefined' ? window.location.origin : ''
        const params = new URLSearchParams()
        params.set('shop', shop!)
        if (host) params.set('host', host)
        params.set('format', 'csv')
        params.set('audit', '1')
        params.set('page', '1')
        params.set('createdAtMin', dateFrom)
        params.set('createdAtMax', dateTo)
        if (segment) params.set('dashboardSegment', segment)
        const res = await authenticatedFetch(`${apiBase}/api/orders?${params.toString()}`)
        const ct = res.headers.get('content-type') || ''
        if (!res.ok || ct.includes('application/json')) {
          let msg = 'The CSV export could not be downloaded.'
          try {
            const d = (await parseResponseJson(res)) as { error?: string; reauthUrl?: string }
            msg = typeof d?.error === 'string' ? d.error : msg
            if (res.status === 401 && d?.reauthUrl) {
              setReauthUrl(d.reauthUrl)
              setAuthError(true)
              return
            }
          } catch {
            /* keep default */
          }
          setAuditExportMessage(msg)
          return
        }
        const text = await res.text()
        const segSuffix = segment ? `-${segment}` : ''
        triggerCsvDownload(`audit-orders-${dateFrom}-to-${dateTo}${segSuffix}.csv`, text, { useBom: false })
      } catch {
        setAuditExportMessage('The CSV export could not be downloaded. Please try again.')
      } finally {
        setAuditExporting(false)
      }
    },
    [authenticatedFetch, authError, dateFrom, dateTo, host, shop, sessionReady]
  )

  const auditExportHint = (
    <Text as="p" variant="bodySm" tone="subdued">
      {DASHBOARD_AUDIT_EXPORT_PERIOD_HINT}
    </Text>
  )

  const applyDateRange = useCallback(
    (from: string, to: string) => {
      if (!/^\d{4}-\d{2}-\d{2}$/.test(from) || !/^\d{4}-\d{2}-\d{2}$/.test(to)) return
      pendingPresetUrlRef.current = `${from}|${to}`
      hasSyncedToServerDefaultRef.current = true
      const datesChanged = from !== dateFrom || to !== dateTo
      if (from !== dateFrom) setDateFrom(from)
      if (to !== dateTo) setDateTo(to)
      if (sessionReady && shop) {
        const next = new URLSearchParams(searchParams.toString())
        next.set('createdAtMin', from)
        next.set('createdAtMax', to)
        if (!next.get('shop')) next.set('shop', shop)
        if (host && !next.get('host')) next.set('host', host)
        if (!host && isPortalSurface) next.delete('host')
        const base = pathname || '/dashboard'
        router.replace(`${base}?${next.toString()}`)
      }
      if (datesChanged) triggerFetch()
    },
    [
      dateFrom,
      dateTo,
      host,
      pathname,
      router,
      searchParams,
      sessionReady,
      shop,
      isPortalSurface,
      triggerFetch,
    ]
  )

  const applyReportingPreset = useCallback(
    (preset: ReportingDatePresetId) => {
      const { from, to } = resolveReportingDatePresetRange(preset, shopDisplayTz)
      pendingPresetUrlRef.current = `${from}|${to}`
      hasSyncedToServerDefaultRef.current = true
      const datesChanged = from !== dateFrom || to !== dateTo
      if (from !== dateFrom) setDateFrom(from)
      if (to !== dateTo) setDateTo(to)
      if (sessionReady) {
        const curMin = (searchParams.get('createdAtMin') ?? '').trim()
        const curMax = (searchParams.get('createdAtMax') ?? '').trim()
        const curShop = searchParams.get('shop')
        const curHost = searchParams.get('host')
        const hostMatches = host ? curHost === host : curHost == null || curHost === ''
        const urlAlreadyMatches = curMin === from && curMax === to && curShop === shop && hostMatches
        if (!urlAlreadyMatches) {
          const next = new URLSearchParams(searchParams.toString())
          next.set('createdAtMin', from)
          next.set('createdAtMax', to)
          if (!next.get('shop')) next.set('shop', shop!)
          if (host && !next.get('host')) next.set('host', host)
          if (!host && isPortalSurface) next.delete('host')
          const base = pathname || '/dashboard'
          router.replace(`${base}?${next.toString()}`)
        }
      }
      if (datesChanged) triggerFetch()
    },
    [dateFrom, dateTo, host, pathname, router, searchParams, shop, shopDisplayTz, sessionReady, isPortalSurface, triggerFetch]
  )

  /**
   * Drill-down URLs (orders, expenses, revenue, metric breakdown). Null when shop/host missing.
   * Must run before any conditional return (Rules of Hooks). Parity: {@link buildRevenueDrilldownUrl}.
   */
  const dashboardDrilldownHrefs = useMemo(() => {
    if (!sessionReady || !shop) return null
    const s = shop
    return {
      orders: {
        total: buildOrdersDrilldownUrl(s, host, dateFrom, dateTo, 'total'),
        fulfilled: buildOrdersDrilldownUrl(s, host, dateFrom, dateTo, 'fulfilled'),
        cancelled: buildOrdersDrilldownUrl(s, host, dateFrom, dateTo, 'cancelled'),
        delivered: buildOrdersDrilldownUrl(s, host, dateFrom, dateTo, 'delivered'),
        returned: buildOrdersDrilldownUrl(s, host, dateFrom, dateTo, 'returned'),
        in_transit: buildOrdersDrilldownUrl(s, host, dateFrom, dateTo, 'in_transit'),
        pending: buildOrdersDrilldownUrl(s, host, dateFrom, dateTo, 'pending'),
        pendingShopWide: buildOrdersPendingShopWideUrl(s, host),
        in_transit_shop_wide: buildOrdersInTransitShopWideUrl(s, host),
      },
      expenses: {
        ads: buildExpensesDrilldownUrl(s, host, dateFrom, dateTo, 'ADS'),
        operational: buildExpensesDrilldownUrl(s, host, dateFrom, dateTo, 'OPERATIONAL'),
      },
      revenue: {
        totalAll: buildRevenueDrilldownUrl(s, host, dateFrom, dateTo, 'total_revenue_all'),
        fulfilled: buildRevenueDrilldownUrl(s, host, dateFrom, dateTo, 'revenue_fulfilled'),
        delivered: buildRevenueDrilldownUrl(s, host, dateFrom, dateTo, 'revenue_delivered'),
      },
      metricBreakdown: {
        grossRevenue: buildMetricBreakdownUrl(s, host, dateFrom, dateTo, 'gross_revenue'),
        totalProfit: buildMetricBreakdownUrl(s, host, dateFrom, dateTo, 'total_profit'),
        governmentTax: buildMetricBreakdownUrl(s, host, dateFrom, dateTo, 'government_tax'),
        courierCharges: buildMetricBreakdownUrl(s, host, dateFrom, dateTo, 'courier_charges'),
        payfastFees: buildMetricBreakdownUrl(s, host, dateFrom, dateTo, 'payfast_fees'),
        inventory: buildMetricBreakdownUrl(s, host, dateFrom, dateTo, 'inventory'),
        totalExpenses: buildMetricBreakdownUrl(s, host, dateFrom, dateTo, 'total_expenses'),
      },
    }
  }, [shop, host, dateFrom, dateTo, sessionReady])

  const dashboardHelpSections = useMemo(() => getDashboardReportingHelpSections(), [])

  const periodTrailingSlot = useMemo(
    () => (
      <InlineStack gap="200" blockAlign="center" wrap>
        {isPreviousPeriodLoading && metrics ? (
          <Text as="span" variant="bodySm" tone="subdued">
            Loading comparison period…
          </Text>
        ) : null}
        {isRefreshing && metrics ? (
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '5px',
              fontSize: '12px',
              color: 'var(--p-color-text-secondary)',
              background: 'var(--p-color-bg-surface-secondary)',
              border: '1px solid var(--p-color-border)',
              borderRadius: '20px',
              padding: '2px 10px',
              lineHeight: '1.4',
              whiteSpace: 'nowrap',
            }}
          >
            <span
              style={{
                width: '6px',
                height: '6px',
                borderRadius: '50%',
                background: 'var(--p-color-text-secondary)',
                opacity: 0.5,
                flexShrink: 0,
              }}
            />
            Updating…
          </span>
        ) : null}
        <ReportingHowThisWorksPopover sections={dashboardHelpSections} disabled={!sessionReady} />
      </InlineStack>
    ),
    [dashboardHelpSections, isPreviousPeriodLoading, isRefreshing, metrics, sessionReady]
  )

  const moversResult = useMemo(() => {
    if (!metrics) {
      return {
        hasComparablePeriod: false,
        improvements: [] as DashboardMoverBullet[],
        setbacks: [] as DashboardMoverBullet[],
      }
    }
    return buildDashboardMovers(
      sliceMetricsForMovers(metrics),
      previousMetrics ? sliceMetricsForMovers(previousMetrics) : null
    )
  }, [metrics, previousMetrics])

  const dashboardDateContextValue = useMemo(
    () => ({
      dateFrom,
      dateTo,
      setDateFrom,
      setDateTo,
      applyReportingPreset,
      reloadDashboard: handleLoadDashboard,
      dashboardLoading: loading,
      shopDisplayTz,
    }),
    [dateFrom, dateTo, applyReportingPreset, handleLoadDashboard, loading, shopDisplayTz]
  )

  const trendDrilldownLinks = useMemo(() => {
    if (!dashboardDrilldownHrefs) return null
    return (
      <>
        <Button variant="plain" url={dashboardDrilldownHrefs.orders.total}>
          Orders
        </Button>
        <Button variant="plain" url={dashboardDrilldownHrefs.revenue.delivered}>
          Delivered revenue
        </Button>
        <Button variant="plain" url={dashboardDrilldownHrefs.metricBreakdown.totalExpenses}>
          Expense breakdown
        </Button>
      </>
    )
  }, [dashboardDrilldownHrefs])

  const getStartedPromptSection = (
    <Layout.Section>
      <Card>
        <InlineStack align="space-between" blockAlign="center" wrap>
          <div style={{ minWidth: 0, flex: '1 1 0%' }}>
            <BlockStack gap="100">
              <Text as="p" variant="headingSm">
                {coreSetupComplete ? 'Review setup guide' : 'New here? Start with the setup guide'}
              </Text>
              <Text as="p" variant="bodySm" tone="subdued">
                {coreSetupComplete
                  ? 'Your core setup is done. Use the guide as a quick checklist for daily workflow and best practices.'
                  : 'Follow the Get Started flow to set up orders, costs, courier APIs, and jobs in the right order.'}
              </Text>
            </BlockStack>
          </div>
          <Button url="/get-started" variant={coreSetupComplete ? 'secondary' : 'primary'}>
            {coreSetupComplete ? 'Review Guide' : 'Open Get Started'}
          </Button>
        </InlineStack>
      </Card>
    </Layout.Section>
  )

  if (authError) {
    return (
      <DashboardDateProvider value={dashboardDateContextValue}>
        <Page title="Dashboard">
          <div className="sigma-page sigma-page--dashboard sigma-density-tight">
          <Layout>
            {getStartedPromptSection}
            <Layout.Section>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '200px', gap: '16px' }}>
                <Spinner size="large" />
                <Text as="p" variant="bodyMd" tone="subdued">Redirecting to authenticate…</Text>
              </div>
            </Layout.Section>
          </Layout>
          </div>
        </Page>
      </DashboardDateProvider>
    )
  }

  // Use same skeleton for "no shop yet" and "loading" to avoid Spinner→skeleton flicker
  const showSkeleton = !sessionReady || loading
  if (showSkeleton) {
    return (
      <DashboardDateProvider value={dashboardDateContextValue}>
        <Page title="Dashboard">
          <div className="sigma-page sigma-page--dashboard sigma-density-tight">
          <Layout>
            {getStartedPromptSection}
            <Layout.Section>
              <Card>
                <DashboardPeriodRangeControl
                  dateFrom={dateFrom}
                  dateTo={dateTo}
                  timeZone={shopDisplayTz}
                  onRangeComplete={applyDateRange}
                  onSelectPreset={applyReportingPreset}
                  onReload={handleLoadDashboard}
                  reloadDisabled
                  disabled={!sessionReady}
                />
              </Card>
            </Layout.Section>
            <Layout.Section>
              <div className="sigma-dashboard-metric-grid">
                {[0, 1, 2, 3, 4].map((i) => (
                  <MetricCardSkeleton key={i} />
                ))}
              </div>
            </Layout.Section>
            <Layout.Section>
              <Card>
                <BlockStack gap="300">
                  <Text as="p" variant="headingSm">Performance over period</Text>
                  <div style={{ height: 140, background: 'var(--p-color-bg-fill-tertiary)', borderRadius: 8 }} aria-hidden />
                </BlockStack>
              </Card>
            </Layout.Section>
            <Layout.Section>
              <div className="sigma-dashboard-metric-grid">
                {[0, 1, 2, 3].map((i) => (
                  <MetricCardSkeleton key={i} />
                ))}
              </div>
            </Layout.Section>
          </Layout>
          </div>
        </Page>
      </DashboardDateProvider>
    )
  }

  if (error) {
    return (
      <DashboardDateProvider value={dashboardDateContextValue}>
        <Page title="Dashboard">
          <div className="sigma-page sigma-page--dashboard sigma-density-tight">
          <Layout>
            {getStartedPromptSection}
          <Layout.Section>
            <Card>
              <BlockStack gap="300">
                <DashboardPeriodRangeControl
                  dateFrom={dateFrom}
                  dateTo={dateTo}
                  timeZone={shopDisplayTz}
                  onRangeComplete={applyDateRange}
                  onSelectPreset={applyReportingPreset}
                  onReload={handleLoadDashboard}
                  disabled={!sessionReady}
                  trailingSlot={
                    <DashboardOrdersExportMenu
                      onExport={(seg) => void handleExportAuditCsv(seg)}
                      loading={auditExporting}
                      disabled={!sessionReady}
                      variant="primary"
                    />
                  }
                />
                {auditExportHint}
              </BlockStack>
            </Card>
          </Layout.Section>
          {auditExportMessage ? (
            <Layout.Section>
              <Banner tone="critical" title="CSV export" onDismiss={() => setAuditExportMessage(null)}>
                <p>{auditExportMessage}</p>
              </Banner>
            </Layout.Section>
          ) : null}
          <Layout.Section>
            <Banner tone="critical" title="Could not load dashboard" action={{ content: 'Retry', onAction: triggerFetch }}>
              <p>{error}</p>
            </Banner>
          </Layout.Section>
        </Layout>
        </div>
        </Page>
      </DashboardDateProvider>
    )
  }

  if (!metrics) {
    if (typeof window !== 'undefined' && process.env.NEXT_PUBLIC_DEBUG_DASHBOARD === '1') {
      console.log('[dashboard] empty state', { reason: '!metrics', pendingMessage: pendingMessage ?? '—' })
    }
    return (
      <DashboardDateProvider value={dashboardDateContextValue}>
        <Page title="Dashboard">
          <div className="sigma-page sigma-page--dashboard sigma-density-tight">
          <Layout>
            {getStartedPromptSection}
          <Layout.Section>
            <Card>
              <BlockStack gap="400">
                <DashboardPeriodRangeControl
                  dateFrom={dateFrom}
                  dateTo={dateTo}
                  timeZone={shopDisplayTz}
                  onRangeComplete={applyDateRange}
                  onSelectPreset={applyReportingPreset}
                  onReload={handleLoadDashboard}
                  disabled={!sessionReady}
                  trailingSlot={
                    <DashboardOrdersExportMenu
                      onExport={(seg) => void handleExportAuditCsv(seg)}
                      loading={auditExporting}
                      disabled={!sessionReady}
                      variant="primary"
                    />
                  }
                />
                {auditExportHint}
                {pendingMessage ? (
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '10px',
                      padding: '12px 14px',
                      borderRadius: '10px',
                      background: 'var(--sigma-primary-soft, #d9f2ef)',
                      border: '1px solid #9fd9d3',
                    }}
                  >
                    <span className="sigma-pulse-dot" aria-hidden="true" />
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                      <Text as="p" variant="bodySm" fontWeight="semibold">Analytics computing</Text>
                      <Text as="p" variant="bodySm" tone="subdued">{pendingMessage}</Text>
                      {orderDiagnostics != null ? (
                        <Text as="p" variant="bodySm" tone="subdued">
                          Orders already in Sigma Sense for this range:{' '}
                          {orderDiagnostics.ordersInDatabaseForRange.toLocaleString()}. If that is lower than Shopify,
                          sync or repair orders first if counts look low.
                        </Text>
                      ) : null}
                    </div>
                  </div>
                ) : (
                  <Text as="p" variant="bodyMd" tone="subdued">
                    No dashboard data yet. Choose a date range and click Reload.
                  </Text>
                )}
              </BlockStack>
            </Card>
          </Layout.Section>
          {auditExportMessage ? (
            <Layout.Section>
              <Banner tone="critical" title="CSV export" onDismiss={() => setAuditExportMessage(null)}>
                <p>{auditExportMessage}</p>
              </Banner>
            </Layout.Section>
          ) : null}
        </Layout>
        </div>
        </Page>
      </DashboardDateProvider>
    )
  }

  const hasNoOrders = metrics.totalOrdersReceived === 0

  const prev = previousMetrics
  const fmt = (n: number, decimals = 0) =>
    n.toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals })
  const fmtRs = (n: number) =>
    `Rs ${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
  const inTransitOrders = metrics.inTransitOrders
  const prevInTransitOrders = prev?.inTransitOrders

  return (
    <DashboardDateProvider value={dashboardDateContextValue}>
      <Page title="Dashboard">
        <div className="sigma-page sigma-page--dashboard sigma-density-tight">
        <Layout>

        {/* ── Dashboard top-level tabs ────────────────────────────────── */}
        <Layout.Section>
          <Tabs tabs={DASHBOARD_TABS} selected={dashTabIndex} onSelect={handleDashboardTabChange} />
        </Layout.Section>

        {/* ── Non-overview tabs: mount only when active ───────────────── */}
        {dashTabIndex === 1 && <AnalyticsOverviewTabContent />}
        {dashTabIndex === 2 && <CouriersTabContent />}
        {dashTabIndex === 3 && <AreasTabContent />}

        {/* ── Overview tab content ─────────────────────────────────────── */}
        {dashTabIndex === 0 && <>

        {/* ── Period + controls (toolbar; meta line below to reduce noise) ─ */}
        <Layout.Section>
          <SigmaPeriodBar sticky={isPortalSurface}>
            <Card padding="0">
              <div className="sigma-page-hero sigma-dashboard-period-hero sigma-dashboard-period-bar">
                <DashboardPeriodRangeControl
                  dateFrom={dateFrom}
                  dateTo={dateTo}
                  timeZone={shopDisplayTz}
                  onRangeComplete={applyDateRange}
                  onSelectPreset={applyReportingPreset}
                  onReload={handleLoadDashboard}
                  disabled={!sessionReady}
                  trailingSlot={periodTrailingSlot}
                />
              </div>
            </Card>
          </SigmaPeriodBar>
        </Layout.Section>
        {/* ── Key metrics (primary surface) ───────────────────────────── */}
        <Layout.Section>
          <Card>
            <BlockStack gap="300">
              <SigmaSectionHeader
                title="Key numbers"
                description="Orders, deliveries, returns, sales, profit — click a tile for matching lists or detail."
              />
              <div className="sigma-dashboard-metric-grid">
                <MetricCard
                  label="Orders placed"
                  value={fmt(metrics.totalOrdersReceived)}
                  previous={prev?.totalOrdersReceived}
                  currentNum={metrics.totalOrdersReceived}
                  higherIsBetter="higher"
                  showAbsoluteDelta
                  href={dashboardDrilldownHrefs?.orders.total}
                />
                <MetricCard
                  label="Delivered orders"
                  value={fmt(metrics.deliveredOrders)}
                  previous={prev?.deliveredOrders}
                  currentNum={metrics.deliveredOrders}
                  higherIsBetter="higher"
                  valueTone="success"
                  showAbsoluteDelta
                  href={dashboardDrilldownHrefs?.orders.delivered}
                />
                <MetricCard
                  label="Return rate"
                  value={`${metrics.rtoRate.toFixed(2)}%`}
                  previous={prev?.rtoRate}
                  currentNum={metrics.rtoRate}
                  higherIsBetter="lower"
                  format="percent"
                  accent={metrics.rtoRate >= OVERVIEW_RTO_ATTENTION_PCT ? 'caution' : undefined}
                />
                <MetricCard
                  label="Sales (all orders)"
                  value={fmtRs(metrics.totalRevenueAll)}
                  previous={prev?.totalRevenueAll}
                  currentNum={metrics.totalRevenueAll}
                  higherIsBetter="higher"
                  format="currency"
                  showAbsoluteDelta
                  href={dashboardDrilldownHrefs?.revenue.totalAll}
                />
                <MetricCard
                  label="Profit after costs"
                  value={fmtRs(metrics.netProfit)}
                  previous={prev?.netProfit}
                  currentNum={metrics.netProfit}
                  higherIsBetter="higher"
                  format="currency"
                  valueTone={metrics.netProfit >= 0 ? 'success' : 'critical'}
                  showAbsoluteDelta
                  href={dashboardDrilldownHrefs?.metricBreakdown.totalProfit}
                  accent="profit"
                  valueEmphasis
                />
              </div>
            </BlockStack>
          </Card>
        </Layout.Section>

        {/* ── Insights (thresholds + movers) ──────────────────────────── */}
        <Layout.Section>
          <DashboardOverviewInsights
            movers={moversResult}
            shop={shop ?? null}
            host={host ?? null}
            dateFrom={dateFrom}
            dateTo={dateTo}
            rtoRatePct={metrics.rtoRate}
            netProfit={metrics.netProfit}
            usagePercentUsed={usage.percentUsed}
            usageOrderLimit={usage.orderLimit}
            billingHref={billingHref}
          />
        </Layout.Section>

        {/* ── Trend + drilldowns ──────────────────────────────────────── */}
        <Layout.Section>
          <DashboardTrendCard
            shop={shop ?? null}
            host={host ?? null}
            dateFrom={dateFrom}
            dateTo={dateTo}
            drilldownLinks={trendDrilldownLinks}
            deferUntilVisible={isPortalSurface}
          />
        </Layout.Section>

        {/* ── Setup guide prompt ──────────────────────────────────────── */}
        {getStartedPromptSection}

        {/* ── Detailed breakdown — nested collapsibles ──────────────── */}
        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <SigmaSectionHeader
                title="More detail"
                description="Optional — open a group when you need extra tiles (fulfillment, costs, totals)."
              />
              <Divider />
              <BlockStack gap="300">
                <Button
                  variant="plain"
                  disclosure={metricsOrdersOpen ? 'up' : 'down'}
                  onClick={() => setMetricsOrdersOpen((v) => !v)}
                >
                  {metricsOrdersOpen ? 'Hide' : 'Show'} order counts
                </Button>
                <Collapsible open={metricsOrdersOpen} id="dashboard-metrics-orders">
                  <div className="sigma-dashboard-metric-grid sigma-dashboard-metric-grid--min-160">
                    <MetricCard
                      label="Fulfilled orders"
                      value={fmt(metrics.totalFulfilledOrders)}
                      previous={prev?.totalFulfilledOrders}
                      currentNum={metrics.totalFulfilledOrders}
                      higherIsBetter="higher"
                      href={dashboardDrilldownHrefs?.orders.fulfilled}
                    />
                    <MetricCard
                      label="Delivered orders"
                      value={fmt(metrics.deliveredOrders)}
                      previous={prev?.deliveredOrders}
                      currentNum={metrics.deliveredOrders}
                      higherIsBetter="higher"
                      valueTone="success"
                      showAbsoluteDelta
                      href={dashboardDrilldownHrefs?.orders.delivered}
                    />
                    <MetricCard
                      label="Cancelled orders"
                      value={fmt(metrics.totalCancelledOrders)}
                      previous={prev?.totalCancelledOrders}
                      currentNum={metrics.totalCancelledOrders}
                      higherIsBetter="lower"
                      href={dashboardDrilldownHrefs?.orders.cancelled}
                    />
                  </div>
                </Collapsible>
                <Divider />
                <Button
                  variant="plain"
                  disclosure={metricsDeliveryOpen ? 'up' : 'down'}
                  onClick={() => setMetricsDeliveryOpen((v) => !v)}
                >
                  {metricsDeliveryOpen ? 'Hide' : 'Show'} returns and in‑transit
                </Button>
                <Collapsible open={metricsDeliveryOpen} id="dashboard-metrics-delivery">
                  <div className="sigma-dashboard-metric-grid">
                    <MetricCard
                      label="Returned orders"
                      value={fmt(metrics.returnedOrders)}
                      previous={prev?.returnedOrders}
                      currentNum={metrics.returnedOrders}
                      higherIsBetter="lower"
                      valueTone="critical"
                      showAbsoluteDelta
                      href={dashboardDrilldownHrefs?.orders.returned}
                    />
                    <MetricCard
                      label="On the way to customers"
                      value={fmt(inTransitOrders)}
                      previous={prevInTransitOrders}
                      currentNum={inTransitOrders}
                      higherIsBetter="lower"
                      href={dashboardDrilldownHrefs?.orders.in_transit}
                    />
                    <MetricCard
                      label="In-transit P/L (actual)"
                      value={fmtRs(metrics.inTransitPipeline?.inRange.actualProfit ?? 0)}
                      currentNum={metrics.inTransitPipeline?.inRange.actualProfit ?? 0}
                      higherIsBetter="higher"
                      format="currency"
                    />
                    <MetricCard
                      label="In-transit profit if all deliver"
                      value={fmtRs(metrics.inTransitPipeline?.inRange.projectedProfitIfDelivered ?? 0)}
                      currentNum={metrics.inTransitPipeline?.inRange.projectedProfitIfDelivered ?? 0}
                      higherIsBetter="higher"
                      format="currency"
                    />
                    <MetricCard
                      label="All in-transit (shop)"
                      value={fmt(metrics.inTransitPipeline?.shopWide.orderCount ?? inTransitOrders)}
                      currentNum={metrics.inTransitPipeline?.shopWide.orderCount ?? 0}
                      higherIsBetter="lower"
                      href={dashboardDrilldownHrefs?.orders.in_transit_shop_wide}
                    />
                    <MetricCard
                      label="Shop in-transit profit (actual)"
                      value={fmtRs(metrics.inTransitPipeline?.shopWide.actualProfit ?? 0)}
                      currentNum={metrics.inTransitPipeline?.shopWide.actualProfit ?? 0}
                      higherIsBetter="higher"
                      format="currency"
                    />
                    <MetricCard
                      label="Open / pending orders"
                      value={fmt(metrics.pendingPipeline?.inRange.orderCount ?? 0)}
                      currentNum={metrics.pendingPipeline?.inRange.orderCount ?? 0}
                      higherIsBetter="lower"
                      href={dashboardDrilldownHrefs?.orders.pending}
                    />
                    <MetricCard
                      label="Pending profit (if all deliver)"
                      value={fmtRs(metrics.pendingPipeline?.inRange.projectedProfitIfDelivered ?? 0)}
                      currentNum={metrics.pendingPipeline?.inRange.projectedProfitIfDelivered ?? 0}
                      higherIsBetter="higher"
                      format="currency"
                    />
                    <MetricCard
                      label="Pending P/L today"
                      value={fmtRs(metrics.pendingPipeline?.inRange.actualProfit ?? 0)}
                      currentNum={metrics.pendingPipeline?.inRange.actualProfit ?? 0}
                      higherIsBetter="higher"
                      format="currency"
                    />
                    <MetricCard
                      label="All open orders (shop)"
                      value={fmt(metrics.pendingPipeline?.shopWide.orderCount ?? 0)}
                      currentNum={metrics.pendingPipeline?.shopWide.orderCount ?? 0}
                      higherIsBetter="lower"
                      href={dashboardDrilldownHrefs?.orders.pendingShopWide}
                    />
                    <MetricCard
                      label="Shop-wide pending profit (if deliver)"
                      value={fmtRs(metrics.pendingPipeline?.shopWide.projectedProfitIfDelivered ?? 0)}
                      currentNum={metrics.pendingPipeline?.shopWide.projectedProfitIfDelivered ?? 0}
                      higherIsBetter="higher"
                      format="currency"
                    />
                  </div>
                </Collapsible>
                <Divider />
                <Button
                  variant="plain"
                  disclosure={metricsRevenueOpen ? 'up' : 'down'}
                  onClick={() => setMetricsRevenueOpen((v) => !v)}
                >
                  {metricsRevenueOpen ? 'Hide' : 'Show'} fulfilled vs delivered sales
                </Button>
                <Collapsible open={metricsRevenueOpen} id="dashboard-metrics-revenue">
                  <div className="sigma-dashboard-metric-grid sigma-dashboard-metric-grid--min-220">
                    <MetricCard
                      label="Sales after fulfillment"
                      value={fmtRs(metrics.totalRevenueFulfilled)}
                      previous={prev?.totalRevenueFulfilled}
                      currentNum={metrics.totalRevenueFulfilled}
                      higherIsBetter="higher"
                      format="currency"
                      href={dashboardDrilldownHrefs?.revenue.fulfilled}
                    />
                    <MetricCard
                      label="Sales after delivery"
                      value={fmtRs(metrics.totalRevenueDelivered)}
                      previous={prev?.totalRevenueDelivered}
                      currentNum={metrics.totalRevenueDelivered}
                      higherIsBetter="higher"
                      format="currency"
                      showAbsoluteDelta
                      href={dashboardDrilldownHrefs?.revenue.delivered}
                    />
                  </div>
                </Collapsible>
                <Divider />
                <Button
                  variant="plain"
                  disclosure={metricsExpensesOpen ? 'up' : 'down'}
                  onClick={() => setMetricsExpensesOpen((v) => !v)}
                >
                  {metricsExpensesOpen ? 'Hide' : 'Show'} tax, shipping, PayFast, ads, and other costs
                </Button>
                <Collapsible open={metricsExpensesOpen} id="dashboard-metrics-expenses">
                  <div className="sigma-dashboard-metric-grid sigma-dashboard-metric-grid--min-180">
                    <MetricCard
                      label="Government tax"
                      value={fmtRs(metrics.governmentTax)}
                      previous={prev?.governmentTax}
                      currentNum={metrics.governmentTax}
                      higherIsBetter="lower"
                      format="currency"
                      href={dashboardDrilldownHrefs?.metricBreakdown.governmentTax}
                    />
                    <MetricCard
                      label="Courier shipping"
                      value={fmtRs(metrics.courierCharges)}
                      previous={prev?.courierCharges}
                      currentNum={metrics.courierCharges}
                      higherIsBetter="lower"
                      format="currency"
                      href={dashboardDrilldownHrefs?.metricBreakdown.courierCharges}
                    />
                    <MetricCard
                      label="PayFast fees"
                      value={fmtRs(metrics.payfastExpenses)}
                      previous={prev?.payfastExpenses}
                      currentNum={metrics.payfastExpenses}
                      higherIsBetter="lower"
                      format="currency"
                      href={dashboardDrilldownHrefs?.metricBreakdown.payfastFees}
                    />
                    <MetricCard
                      label="Ads"
                      value={fmtRs(metrics.adsExpenses)}
                      previous={prev?.adsExpenses}
                      currentNum={metrics.adsExpenses}
                      higherIsBetter="lower"
                      format="currency"
                      href={dashboardDrilldownHrefs?.expenses.ads}
                    />
                    <MetricCard
                      label="Operational"
                      value={fmtRs(metrics.operationalExpenses)}
                      previous={prev?.operationalExpenses}
                      currentNum={metrics.operationalExpenses}
                      higherIsBetter="lower"
                      format="currency"
                      href={dashboardDrilldownHrefs?.expenses.operational}
                    />
                    <MetricCard
                      label="Inventory"
                      value={fmtRs(metrics.inventoryExpenses)}
                      previous={prev?.inventoryExpenses}
                      currentNum={metrics.inventoryExpenses}
                      higherIsBetter="lower"
                      format="currency"
                      href={dashboardDrilldownHrefs?.metricBreakdown.inventory}
                    />
                  </div>
                </Collapsible>
                <Divider />
                <Button
                  variant="plain"
                  disclosure={metricsFinancialOpen ? 'up' : 'down'}
                  onClick={() => setMetricsFinancialOpen((v) => !v)}
                >
                  {metricsFinancialOpen ? 'Hide' : 'Show'} profit checklist
                </Button>
                <Collapsible open={metricsFinancialOpen} id="dashboard-metrics-financial">
                  <div className="sigma-dashboard-metric-grid sigma-dashboard-metric-grid--min-220">
                    <MetricCard
                      label="Sales (delivered)"
                      value={fmtRs(metrics.totalRevenueDelivered)}
                      previous={prev?.totalRevenueDelivered}
                      currentNum={metrics.totalRevenueDelivered}
                      higherIsBetter="higher"
                      format="currency"
                      showAbsoluteDelta
                      href={dashboardDrilldownHrefs?.metricBreakdown.grossRevenue}
                    />
                    <MetricCard
                      label="All costs"
                      value={fmtRs(metrics.totalExpenses)}
                      previous={prev?.totalExpenses}
                      currentNum={metrics.totalExpenses}
                      higherIsBetter="lower"
                      format="currency"
                      showAbsoluteDelta
                      href={dashboardDrilldownHrefs?.metricBreakdown.totalExpenses}
                    />
                    <MetricCard
                      label="Profit"
                      value={fmtRs(metrics.netProfit)}
                      previous={prev?.netProfit}
                      currentNum={metrics.netProfit}
                      higherIsBetter="higher"
                      format="currency"
                      valueTone={metrics.netProfit >= 0 ? 'success' : 'critical'}
                      showAbsoluteDelta
                      href={dashboardDrilldownHrefs?.metricBreakdown.totalProfit}
                    />
                  </div>
                </Collapsible>
              </BlockStack>
            </BlockStack>
          </Card>
        </Layout.Section>

        {/* ── Quick actions (after detail; export + deep links) ───────── */}
        <Layout.Section>
          <DashboardQuickActions
            shop={shop ?? null}
            host={host ?? null}
            onExportAudit={(seg) => void handleExportAuditCsv(seg)}
            exportLoading={auditExporting}
            exportDisabled={!sessionReady}
            showSkuCostHint={!coreSetupComplete}
            showCourierHint={!coreSetupComplete}
          />
        </Layout.Section>

        {/* ── UsageBar ─────────────────────────────────────────────────── */}
        <Layout.Section>
          <UsageBar
            plan={usage.plan}
            usedOrders={usage.usedOrders}
            orderLimit={usage.orderLimit}
            percentUsed={usage.percentUsed}
            loading={usage.loading}
            billingHref={billingHref}
          />
        </Layout.Section>
        {usage.percentUsed >= 100 && usage.orderLimit !== null && (
          <Layout.Section>
            <Banner
              tone="critical"
              title="You have reached your monthly order limit."
              action={{
                content: 'Upgrade Plan',
                url: billingHref,
              }}
            >
              <p>Further order imports are paused until your next billing cycle or you upgrade your plan.</p>
            </Banner>
          </Layout.Section>
        )}

        {/* ── No orders in period ──────────────────────────────────────── */}
        {hasNoOrders && (
          <Layout.Section>
            <Card>
              <SigmaEmptyState
                title="No orders in this period"
                description="There are no orders created in the selected range. Try different dates or confirm orders are syncing from Shopify."
              />
            </Card>
          </Layout.Section>
        )}

        <Layout.Section>
          <BlockStack gap="200">{auditExportHint}</BlockStack>
        </Layout.Section>
        {auditExportMessage ? (
          <Layout.Section>
            <Banner tone="critical" title="CSV export" onDismiss={() => setAuditExportMessage(null)}>
              <p>{auditExportMessage}</p>
            </Banner>
          </Layout.Section>
        ) : null}

        </>}

      </Layout>
      </div>
      </Page>
    </DashboardDateProvider>
  )
}
